import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Typography,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import qhsLogo from '../../../assets/icons/qlab_blue_logo.svg';
import AddInstantSurveyModal from './AddInstantSurveyModal';
import { enqueueSnackbar, SNACKBAR_VARIANTS, getTransitionWithVariable } from '../../../redux_store';
import copy from 'copy-to-clipboard';
import { useStyles } from './SurveyContent/styles';
import { generateQRCode } from './SurveyContent/qrCodeUtils';
import { createSurveyActionHandlers } from './SurveyContent/surveyActions';
import DeleteConfirmationModal from './SurveyContent/components/DeleteConfirmationModal';
import StatusChangeConfirmationModal from './SurveyContent/components/StatusChangeConfirmationModal';
import HeaderWidget from './SurveyContent/widgets/HeaderWidget';
import BasicConfigurationWidget from './SurveyContent/widgets/BasicConfigurationWidget';
import LinkAndQRCodeWidget from './SurveyContent/widgets/LinkAndQRCodeWidget';
import SubmissionsStatusWidget from './SurveyContent/widgets/SubmissionsStatusWidget';
import { SURVEY_STATUS } from './constants';

const SurveyContent = ({
  selectedSurvey,
  instantSurveys,
  setFieldValue,
  values,
  setSubmitting,
  editAccount,
  onSurveySelect,
}) => {
  const dispatch = useDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalStep, setDeleteModalStep] = useState(1);

  // Status change confirmation modals
  const [statusConfirmModal, setStatusConfirmModal] = useState({
    open: false,
    type: '',
    loading: false,
    step: 1,
  });

  // Get instant surveys from Redux store
  const {
    assessmentInstruments,
  } = useSelector((state) => state.instantAssessment);

  const currentSurvey = useMemo(() =>
      instantSurveys.find(survey => survey.key === selectedSurvey),
    [selectedSurvey, instantSurveys],
  );

  // Helper function to get survey type name from ID
  const getSurveyTypeName = (surveyTypeId) => {
    const instrument = assessmentInstruments.find(inst => inst.id === surveyTypeId);
    return instrument ? instrument.name : surveyTypeId;
  };

  const classes = useStyles();
  const { en: { notifications, instantSurvey } } = useSelector((state) => state.i18n);
  const qrRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const [qrCode, setQrCode] = useState(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState('draft');

  // Create action handlers
  const {
    handleLaunchSurvey,
    handleDeactivateSurvey,
    handleRelaunchSurvey,
    handleArchiveSurvey,
    handleDeleteSurvey,
    handleConfirmDelete,
    handleConfirmStatusChange,
  } = createSurveyActionHandlers({
    setStatusConfirmModal,
    setIsDeleteModalOpen,
    currentSurvey,
    selectedSurvey,
    instantSurveys,
    values,
    setFieldValue,
    setAssessmentStatus,
    editAccount,
    setSubmitting,
    onSurveySelect,
    setIsDeleting,
    deleteModalStep,
    setDeleteModalStep,
    statusConfirmModal,
  });

  useEffect(() => {
    // Update formData and link when selectedSurvey changes
    if (currentSurvey) {
      // Set assessment status from survey data or default to draft
      setAssessmentStatus(currentSurvey.status || 'draft');

      // Use the real link from the survey
      setGeneratedLink(currentSurvey.link || `https://qhs.ai/survey/${currentSurvey.code}`);
    }
  }, [currentSurvey]);

  // Scroll to top when survey changes
  useEffect(() => {
    if (scrollableContentRef.current && selectedSurvey) {
      scrollableContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedSurvey]);

  // Preload logo to ensure it's available when generating QR code
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setLogoLoaded(true);
    };
    img.onerror = () => {
      console.warn('QR logo failed to load, generating QR code without logo');
      setLogoLoaded(true); // Still allow QR generation without logo
    };
    img.src = qhsLogo;
  }, []);

  useEffect(() => {
    if (generatedLink && qrRef.current && logoLoaded) {
      generateQRCode(generatedLink, qrRef, logoLoaded, setQrCode);
    }
  }, [generatedLink, logoLoaded]);

  useEffect(() => {
    // Generate QR code when status becomes active and QR section is shown
    if (assessmentStatus === SURVEY_STATUS.active && generatedLink && qrRef.current && logoLoaded) {
      setTimeout(() => {
        generateQRCode(generatedLink, qrRef, logoLoaded, setQrCode);
      }, 100);
    }
  }, [assessmentStatus, logoLoaded]);

  // Regenerate QR code on window resize
  useEffect(() => {
    const handleResize = () => {
      if (assessmentStatus === SURVEY_STATUS.active && generatedLink && qrRef.current && logoLoaded) {
        setTimeout(() => {
          generateQRCode(generatedLink, qrRef, logoLoaded, setQrCode);
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [assessmentStatus, generatedLink, logoLoaded]);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const downloadQR = () => {
    if (qrCode) {
      qrCode.download({ name: 'qhs-assessment-qr', extension: 'png' });
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await copy(generatedLink);
      setLinkCopied(true);
      dispatch(
        enqueueSnackbar({
          message: notifications.dataCopied,
          options: {
            variant: SNACKBAR_VARIANTS.success,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
        })
      );

      // Reset copied state after 3 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: notifications.dataNotCopied,
          options: {
            variant: SNACKBAR_VARIANTS.error,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
        })
      );
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setDeleteModalStep(1);
    }
  };

  const handleCloseStatusModal = () => {
    if (!statusConfirmModal.loading) {
      setStatusConfirmModal({ open: false, type: '', loading: false, step: 1 });
    }
  };

  if (!selectedSurvey) {
    return (
      <div className={classes.container}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className={classes.emptyState}>
            <Typography className={classes.emptyTitle}>
              {instantSurvey.emptyStateTitle}
            </Typography>
            <Typography className={classes.emptyText}>
              {instantSurvey.emptyStateDescription}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.container}>
        <HeaderWidget
          currentSurvey={currentSurvey}
          assessmentStatus={assessmentStatus}
          getSurveyTypeName={getSurveyTypeName}
          handleLaunchSurvey={handleLaunchSurvey}
          handleDeleteSurvey={handleDeleteSurvey}
          handleDeactivateSurvey={handleDeactivateSurvey}
          handleRelaunchSurvey={handleRelaunchSurvey}
          handleArchiveSurvey={handleArchiveSurvey}
          classes={classes}
        />

        <div ref={scrollableContentRef} className={classes.scrollableContent}>
          <BasicConfigurationWidget
            currentSurvey={currentSurvey}
            assessmentStatus={assessmentStatus}
            getSurveyTypeName={getSurveyTypeName}
            handleEditClick={handleEditClick}
            classes={classes}
          />

          <LinkAndQRCodeWidget
            currentSurvey={currentSurvey}
            assessmentStatus={assessmentStatus}
            generatedLink={generatedLink}
            linkCopied={linkCopied}
            qrRef={qrRef}
            copyLinkToClipboard={copyLinkToClipboard}
            downloadQR={downloadQR}
            classes={classes}
          />

          <SubmissionsStatusWidget
            currentSurvey={currentSurvey}
            assessmentStatus={assessmentStatus}
            classes={classes}
          />
        </div>
      </div>

      {/* Edit Survey Modal */}
      {currentSurvey && (
        <AddInstantSurveyModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          setFieldValue={setFieldValue}
          values={values}
          setSubmitting={setSubmitting}
          editAccount={editAccount}
          editMode={true}
          surveyToEdit={currentSurvey}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        deleteModalStep={deleteModalStep}
        currentSurvey={currentSurvey}
        classes={classes}
      />

      {/* Status Change Confirmation Modal */}
      <StatusChangeConfirmationModal
        open={statusConfirmModal.open}
        onClose={handleCloseStatusModal}
        onConfirm={handleConfirmStatusChange}
        statusConfirmModal={statusConfirmModal}
        currentSurvey={currentSurvey}
        classes={classes}
      />
    </>
  );
};

export default SurveyContent;
